class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', event => {
      event.preventDefault();
      if (window.CartFlow?.locked || this.closest('.cart-item--removing')) return;
      this.closest('cart-items, cart-drawer-items')?.updateQuantity(this.dataset.index, 0);
    });
  }
}
if (!customElements.get('cart-remove-button')) customElements.define('cart-remove-button', CartRemoveButton);

// 商品行立即收起；更新期间删除键仍接住点击，不能让点击穿到标题链接。
// 全局捕获监听在抽屉整段刷新后仍保留；解锁后只保护商品链接，不延迟下一次删除。
(function () {
  let wasLocked = !!window.CartFlow?.locked;
  let protectUntil = 0;
  function syncInteraction() {
    const isLocked = !!window.CartFlow?.locked;
    // 显示过期时保留离开页面及点击删除触发 recoverDisplay(true) 的恢复入口。
    if (wasLocked && !isLocked) protectUntil = window.CartFlow?.displayStale ? 0 : performance.now() + 350;
    wasLocked = isLocked;
    document.querySelectorAll('cart-items cart-remove-button, cart-drawer-items cart-remove-button').forEach(remove => {
      const disabled = isLocked || !!remove.closest('.cart-item--removing');
      remove.setAttribute('aria-disabled', String(disabled));
      remove.querySelector('button, a')?.setAttribute('aria-disabled', String(disabled));
    });
  }
  document.addEventListener('cart:busy', syncInteraction);
  document.addEventListener('cart:rendered', syncInteraction);
  document.addEventListener('click', event => {
    const link = event.target.closest('.cart-item__name, .cart-item__img_link, .cart-item__media a[href]');
    if (!link?.closest('cart-items, cart-drawer-items')) return;
    syncInteraction();
    if (!window.CartFlow?.locked && performance.now() >= protectUntil) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
  syncInteraction();
})();

// A removal collapses at once so the shopper is not left watching an unchanged row.
// The geometry is inline because the sections' own .cart-item rules outrank a plain
// class, and refresh() re-renders from the server, restoring a row that was rejected.
function collapseRow(row) {
  row.style.boxSizing = 'border-box';
  row.style.height = row.getBoundingClientRect().height + 'px';
  row.classList.add('cart-item--removing');
  requestAnimationFrame(() => Object.assign(row.style, {
    height: '0px', opacity: '0', paddingTop: '0px', paddingBottom: '0px',
    marginTop: '0px', marginBottom: '0px', borderTopWidth: '0px', borderBottomWidth: '0px'
  }));
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', event => this.onChange(event));
  }
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      if (event.source !== 'cart-flow' && !event.cartFlowManaged) this.onCartUpdate();
    });
  }
  disconnectedCallback() { this.cartUpdateUnsubscriber?.(); }
  onChange(event) {
    if (!event.target.matches('.quantity__input[data-index]')) return;
    return this.updateQuantity(event.target.dataset.index, event.target.value);
  }
  async onCartUpdate() {
    try { await CartFlow.refresh(); return true; }
    catch (e) { CartFlow.error(e.message); return false; }
  }
  onCartDrawerUpdate() { return this.onCartUpdate(); }
  async removeVariant(variantId) {
    // Used by sample selectors: never synthesize clicks on every row button.
    let host = this;
    const rows = () => {
      if (!host.isConnected) host = document.querySelector(this.localName);
      return [...(host?.querySelectorAll('.cart-item') || [])].filter(row => row.dataset.itemId === String(variantId));
    };
    const count = rows().length;
    for (let i = 0; i < count; i++) {
      const row = rows()[0];
      if (!row) break;
      const index = row.querySelector('.quantity__input')?.dataset.index;
      if (!index || !(await host.updateQuantity(index, 0))) return false;
    }
    return true;
  }
  async updateQuantity(line, quantity) {
    if (CartFlow.displayStale) { CartFlow.recoverDisplay(true); return false; }
    const row = [...this.querySelectorAll('.cart-item')].find(item => item.querySelector('[data-index]')?.dataset.index === String(line));
    const input = row?.querySelector('.quantity__input');
    if (!row || !input || CartFlow.checkout) return false;
    const amount = Number(quantity);
    if (String(quantity).trim() === '' || !Number.isSafeInteger(amount) || amount < 0 || (amount > 0 && !input.checkValidity())) {
      input.value = input.defaultValue;
      CartFlow.error('Please enter a valid whole-number quantity.');
      return false;
    }
    const key = row.dataset.key;
    if (!key) { CartFlow.error('Please reload your cart before changing this item.'); return false; }
    if (input.dataset.pending === String(amount)) return true;
    input.dataset.pending = String(amount);
    CartFlow.error('');
    row.setAttribute('aria-busy', 'true');
    if (amount === 0) collapseRow(row);
    let message = '';
    try {
      const response = await fetch(CartFlow.url('cart/change.js'), {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({id: key, quantity: amount})
      });
      const cart = await CartFlow.readMutation(response);
      if (!response.ok || cart.errors || cart.status) throw new Error(cart.description || cart.errors || cart.message || window.cartStrings.error);
      const changed = cart.items.find(item => item.key === key);
      if (amount > 0 && changed && changed.quantity !== amount) message = window.cartStrings.quantityError.replace('[quantity]', changed.quantity);
      await CartFlow.refresh();
      return true;
    } catch (e) {
      message = e.message || window.cartStrings.error;
      try { await CartFlow.refresh(); } catch (_) { /* Keep the original error. */ }
      return false;
    } finally {
      delete input.dataset.pending;
      row.removeAttribute('aria-busy');
      if (message) CartFlow.error(message);
    }
  }
}
if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);

// <cart-note>（订单备注）定义在 assets/component-cart-note.js：原来这里边打边存，每停顿一下就触发一次购物车整段重绘。
